import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { RelewiseFilterService } from '../services/FilterService';

export interface FiltersResponse {
    priceRange: { minPrice: number; maxPrice: number };
    categories: { id: string; displayName: string }[];
    sizes: { value: string; hits: number }[];
    colorGroups: { value: string; hits: number }[];
}

const EMPTY_FILTERS_RESPONSE: FiltersResponse = {
    priceRange: { minPrice: 0, maxPrice: 0 },
    categories: [],
    sizes: [],
    colorGroups: []
};

export class GetFiltersDataInput implements IActionInput {
    public categoryRecordId: string | undefined;
    public searchTerm: string | undefined;

    public constructor(categoryRecordId: string | undefined, searchTerm: string | undefined) {
        this.categoryRecordId = categoryRecordId;
        this.searchTerm = searchTerm;
    }

    public getCacheKey = () => `RelewiseFilters-${this.categoryRecordId ?? 'none'}-${this.searchTerm ?? 'none'}`;

    public getCacheObjectType = () => 'RelewiseFilters';

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetFiltersDataInput => {
    const categoryRecordId = inputData.requestContext.urlTokens.recordId;
    const searchTerm = (inputData.requestContext.query?.q ?? '').trim() || undefined;

    return new GetFiltersDataInput(categoryRecordId, searchTerm);
};

export async function getFiltersData(input: GetFiltersDataInput, ctx: IActionContext): Promise<FiltersResponse> {
    if (!input.categoryRecordId && !input.searchTerm) {
        const message =
            '[getProductListFiltersData] Missing category RecordId or search term. Need at least one to make filters request. Returning empty filters.';
        console.error(message);
        ctx.telemetry.error(message);
        return EMPTY_FILTERS_RESPONSE;
    }

    const filterService = new RelewiseFilterService(ctx.requestContext, ctx.telemetry);
    const result = await filterService.getFilters(input.categoryRecordId, input.searchTerm);
    return result ?? EMPTY_FILTERS_RESPONSE;
}

export const getFiltersDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/custom/get-filters',
    action: <IAction<FiltersResponse>>getFiltersData,
    input: createInput
});

export default getFiltersDataAction;
